import React from 'react';
import Table, { TableAction } from '../../components/common/Table';
import { InvoiceBatchDTO } from '../../services/apiClient';
import { convertToUserTime } from '../../utils/dateUtils/convertToUserTime';
import { getBadgeColors } from '../../utils/displayUtils';
import Badge from '../common/Badge';
import createInvoiceBatchActions from '../actionMenus/InvoiceBatchActions';

interface InvoiceBatchTableProps {
  data: InvoiceBatchDTO[];
  handleDelete?: (row: InvoiceBatchDTO) => void;
  handleViewBatchDetail: (row: InvoiceBatchDTO) => void;
  handleSendRenewalNotices: (row: InvoiceBatchDTO) => void;
  handleAddToNetcashBatch: (row: InvoiceBatchDTO) => void;
  currentPage: number;
  totalPages: number;
  setCurrentPage: (page: number) => void;
  pageSize: number;
  setPageSize: (size: number) => void;
  isLoading: boolean;
}

const InvoiceBatchTable: React.FC<InvoiceBatchTableProps> = ({
  data,
  handleDelete,
  handleViewBatchDetail,
  handleSendRenewalNotices,
  handleAddToNetcashBatch,
  currentPage,
  totalPages,
  setCurrentPage,
  pageSize,
  setPageSize,
  isLoading,
}) => {
  const getMonthName = (month: number) => {
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return monthNames[month - 1] || '';
  };

  return (
    <Table
      columns={[
        {
          label: 'Period',
          key: 'period',
          render: (row: InvoiceBatchDTO) => (
            <span>{row.year} {getMonthName(row.month ?? 0)}</span>
          ),
        },

        {
          label: 'Batch No',
          key: 'batchNo',
        },

        {
          label: 'Total Created',
          key: 'totalCreated',
        },

        {
          label: 'Total Paid',
          key: 'totalPaid',
        },

        {
          label: 'Status',
          key: 'invoiceStatusId',
          render: (row: InvoiceBatchDTO) => {
            const base = row.invoiceStatus?.colourGrading?.colour ?? '#ffffffff';
            const { bg, text } = getBadgeColors(base, 0.18, 0.4);
            return base ? (
              <Badge
                label={row.invoiceStatus?.title || 'Unknown'}
                variant="custom"
                customBgColor={bg}
                customTextColor={text}
              />
            ) : (
              <span>{row.invoiceStatus?.title || 'Unknown'}</span>
            );
          },
        },

        {
          label: 'Added',
          key: 'createdUtc',
          format: (value: Date) => convertToUserTime(value?.toString() ?? ''),
        },

        {
          label: 'Renewals Sent',
          key: 'renewalEmailsSentUtc',
          render: (row: InvoiceBatchDTO) =>
            row.renewalEmailsSentUtc ? (
              <span>{convertToUserTime(row.renewalEmailsSentUtc.toString())}</span>
            ) : (
              <span className="text-gray-400">-</span>
            ),
        },

      ]}
      data={data}
      actions={{
        ...(handleDelete ? { onDelete: handleDelete } : {}),
        extra: (row) =>
          createInvoiceBatchActions({
            onViewBatchDetail: handleViewBatchDetail,
            onSendRenewalNotices: handleSendRenewalNotices,
            onAddToNetcashBatch: handleAddToNetcashBatch,
          })(row),
      }}
      currentPage={currentPage}
      totalPages={totalPages}
      onPageChange={setCurrentPage}
      pageSize={pageSize}
      onPageSizeChange={setPageSize}
      isLoading={isLoading}
    />
  );
};

export default InvoiceBatchTable;
